// Reine Rechenlogik für das Anschreiben-Layout. Kein DOM-Zugriff, damit testbar.

// Blöcke, die am rechten Textrand verankert sind (DIN 5008: Absender und Datum).
export const AN_RECHTSBUENDIG = ['absender', 'datum'];

// Anfangsausrichtung je Block. Stand bisher fest im Stylesheet und war deshalb
// für die Werkzeugleiste unsichtbar.
export const AN_START_AUSRICHTUNG = {
  absender: 'right',
  empfaenger: 'left',
  datum: 'right',
  betreff: 'left',
  text: 'justify',
};

/**
 * Messwerte aus dem Fluss-Klon in Blockkoordinaten übersetzen.
 * @param {Object} rects  { [blockKey]: { top, left, width } } relativ zur .anschreiben-Wurzel
 * @param {number} contentWidth  Breite des Textbereichs in Pixeln (165mm)
 */
export function startLayoutAusRects(rects, contentWidth) {
  const out = {};
  for (const [key, r] of Object.entries(rects || {})) {
    if (!r || !r.width) continue;
    // Aufrunden, nicht runden. Die gemessene Textbreite ist fast nie ganzzahlig; ein
    // abgerundeter Wert ist ein Haar zu schmal und schiebt das letzte Wort in eine
    // zweite Zeile ("Bewerbung als / Küchenmonteur"). Ein Haar zu breit schadet nie.
    const width = Math.ceil(r.width);
    out[key] = AN_RECHTSBUENDIG.includes(key)
      ? { top: Math.round(r.top), left: Math.round(contentWidth - width), width }
      : { top: Math.round(r.top), left: Math.round(r.left), width };
  }
  return out;
}

/**
 * Gemessene Startpositionen in den gespeicherten Stand einmischen.
 * Kernregel des Canva-Modells: Ein Block mit `dragged: true` wird nie angefasst.
 * Gibt die ursprüngliche Referenz zurück, wenn sich nichts ändert (spart Renders).
 */
export function mergeLayout(gespeichert = {}, gemessen = {}) {
  const out = { ...gespeichert };
  let geaendert = false;
  for (const [key, mess] of Object.entries(gemessen)) {
    const alt = gespeichert[key];
    if (alt?.dragged) continue;
    if (alt && alt.top === mess.top && alt.left === mess.left && alt.width === mess.width) continue;
    out[key] = { ...alt, ...mess };
    geaendert = true;
  }
  return geaendert ? out : gespeichert;
}

/** Tiefste Blockunterkante in Pixeln — die ehrliche Länge des Briefes. */
export function layoutHoehe(offsets = {}, hoehen = {}) {
  let unterkante = 0;
  for (const [key, o] of Object.entries(offsets)) {
    const h = hoehen[key];
    if (!o || h == null) continue;
    unterkante = Math.max(unterkante, o.top + h);
  }
  return unterkante;
}

/**
 * Größenänderung mit fester Gegenkante — die Ankerregel des Canva-Modells.
 *
 * Seitengriffe (`ml`, `mr`) ändern nur die Breite, der Text bricht neu um, die
 * Schrift bleibt. Waagerechte Griffe (`tm`, `bm`) ändern nur die Höhe. Eckgriffe
 * skalieren gleichmäßig: die Höhe folgt der Breite und die Schrift wächst im selben
 * Verhältnis. In allen Fällen bleibt die gegenüberliegende Kante bzw. Ecke exakt stehen.
 *
 * @param {string} griff  'tl' | 'tm' | 'tr' | 'ml' | 'mr' | 'bl' | 'bm' | 'br'
 * @param {Object} start  { top, left, width, height } vor dem Ziehen
 * @param {Object} delta  { x, y } Zeigerbewegung seit dem Aufsetzen
 * @param {number} minBreite
 * @param {number} minHoehe
 * @returns {{ top:number, left:number, width:number, hoehe:number, skala:number }}
 */
export function resizeMitAnker({ griff, start, delta, minBreite = 40, minHoehe = 24 }) {
  const linksAngefasst = griff[1] === 'l';
  const obenAngefasst  = griff[0] === 't';
  // `tm` und `bm` fassen die waagerechte Kante an. Sie stehen vorn wie die Ecken, sind
  // aber keine — deshalb reicht der erste Buchstabe zur Unterscheidung nicht aus.
  const istSenkrecht = griff[1] === 'm';
  const istEcke = (griff[0] === 't' || griff[0] === 'b') && !istSenkrecht;

  if (istSenkrecht) {
    const roheHoehe = start.height + (obenAngefasst ? -delta.y : delta.y);
    const hoehe = Math.max(minHoehe, Math.round(roheHoehe));
    const top = obenAngefasst ? Math.round(start.top + start.height - hoehe) : start.top;
    return { top, left: start.left, width: start.width, hoehe, skala: 1 };
  }

  // Zieht man links, wird die Breite kleiner, wenn der Zeiger nach rechts geht.
  const roheBreite = start.width + (linksAngefasst ? -delta.x : delta.x);
  const width = Math.max(minBreite, Math.round(roheBreite));

  // Die feste Kante bestimmt, wohin die bewegliche rutscht.
  const left = linksAngefasst ? Math.round(start.left + start.width - width) : start.left;

  if (!istEcke) {
    return { top: start.top, left, width, hoehe: start.height, skala: 1 };
  }

  const skala = width / start.width;
  const hoehe = Math.round(start.height * skala);
  const top = obenAngefasst ? Math.round(start.top + start.height - hoehe) : start.top;
  return { top, left, width, hoehe, skala };
}

/**
 * Rechnet durchgehende Blockkoordinaten in seitenrelative um — der Übergang zu
 * echten Seiten (Stufe 3).
 *
 * Bisher lag der ganze Brief in **einem** langen Koordinatensystem, und jede Seite
 * zeigte denselben Fluss, nur verschoben. Weil dabei am Blattrand statt am Textbereich
 * geschnitten wurde, überlappten sich zwei benachbarte Seiten um oberen plus unteren
 * Rand — derselbe Streifen erschien zweimal. Ab jetzt gehört jeder Block zu genau einer
 * Seite und zählt von deren Oberkante. Damit kann sich nichts mehr überlappen.
 *
 * Die Funktion ist **wiederholbar**: Blöcke, die schon eine `seite` haben, bleiben
 * unangetastet. So kann sie gefahrlos zweimal über dieselben Daten laufen.
 *
 * @param {Object} offsets  gespeicherte Blockpositionen
 * @param {number} pageH    Höhe des Textbereichs einer Seite in Pixeln
 * @returns {{ offsets: Object, seiten: number }}
 */
export function aufSeitenVerteilen(offsets = {}, pageH) {
  if (!pageH) return { offsets, seiten: 1 };

  const out = {};
  let hoechsteSeite = 0;

  for (const [key, o] of Object.entries(offsets)) {
    if (!o || typeof o !== 'object' || o.top == null) { out[key] = o; continue; }
    if (o.seite != null) {
      out[key] = o;
      hoechsteSeite = Math.max(hoechsteSeite, o.seite);
      continue;
    }
    // Über dem Blatt gibt es keine Seite −1: Ein solcher Block bleibt auf Seite 0 und
    // behält sein negatives `top`, sonst spränge er beim Umrechnen nach unten.
    const seite = Math.max(0, Math.floor(o.top / pageH));
    out[key] = { ...o, seite, top: o.top - seite * pageH };
    hoechsteSeite = Math.max(hoechsteSeite, seite);
  }

  return { offsets: out, seiten: hoechsteSeite + 1 };
}

/**
 * Wohin gehört ein Block, nachdem er senkrecht verschoben wurde?
 *
 * Seit Stufe 3 zählt `top` von der Oberkante des Textbereichs **seiner** Seite. Wer
 * einen Block ein Blatt tiefer zieht, meint aber eine andere Seite — also muss beim
 * Loslassen umgerechnet werden.
 *
 * Von Textoberkante zu Textoberkante liegt immer derselbe Abstand:
 *
 * ```
 *   ┌─ Textbereich Seite N ─┐  pageH
 *   ├─ unterer Rand ────────┤  randUnten
 *   ╌╌ Lücke zwischen Blättern  luecke
 *   ├─ oberer Rand ─────────┤  randOben
 *   └─ Textbereich Seite N+1
 * ```
 *
 * Landet die Oberkante im unteren Rand, gehört der Block **noch** zur alten Seite — er
 * liegt ja auf ihrem Papier. Erst in der Lücke beginnt die nächste. Über dem ersten
 * Blatt gibt es keine Seite −1; dort bleibt es bei Seite 0 mit negativer Höhe.
 *
 * @param {Object} block    { seite, top } vor der Bewegung
 * @param {number} versatzY zurückgelegter Weg nach unten (negativ = nach oben)
 * @param {Object} mass     { pageH, randOben, randUnten, luecke }
 * @returns {{ seite:number, top:number }}
 */
export function seiteNachVerschieben({ seite = 0, top = 0 }, versatzY = 0, mass = {}) {
  const { pageH, randOben = 0, randUnten = 0, luecke = 0 } = mass;
  if (!pageH) return { seite, top: top + versatzY };

  const schritt = pageH + randUnten + luecke + randOben;
  const abs = seite * schritt + top + versatzY;
  if (abs < 0) return { seite: 0, top: abs };

  const band = Math.floor(abs / schritt);
  const imBand = abs - band * schritt;
  const ziel = imBand <= pageH + randUnten ? band : band + 1;
  return { seite: ziel, top: abs - ziel * schritt };
}


/**
 * Gehört eine Änderung noch zur laufenden Bedienfolge?
 *
 * Zusammenhängende Bedienungen — Pfeiltasten gedrückt halten, am Farbregler ziehen —
 * tragen eine Kennung. Kommt kurz hintereinander zweimal dieselbe, ist es eine einzige
 * Bedienung und verdient einen einzigen Rückgängig-Schritt. Änderungen ohne Kennung
 * (ein Zug, eine Größenänderung) sind immer für sich abgeschlossen.
 *
 * @param {Object} vorher   { folge, zeit } der vorigen Änderung
 * @param {string} folge    Kennung der neuen Änderung, `null` wenn sie allein steht
 * @param {number} jetzt    Zeitstempel der neuen Änderung
 * @param {number} ruhezeit Wie lange eine Folge nach der letzten Änderung offen bleibt
 */
export function gehoertZurFolge(vorher, folge, jetzt, ruhezeit) {
  if (!folge || !vorher) return false;
  return folge === vorher.folge && jetzt - vorher.zeit < ruhezeit;
}

/**
 * Ragt ein Block über einen Rahmen hinaus? Schon ein Millimeter genügt.
 *
 * Zwei Rahmen werden damit geprüft, derselbe Maßstab für beide: der Sicherheitsrand
 * (dann leuchtet der Rand rot) und die Blattkante selbst (dann gilt der Block als
 * abgelegt und erscheint nicht im fertigen Dokument). Genau auf der Linie zählt
 * noch als drinnen.
 *
 * @param {Object} block   { left, top, width, height } im Koordinatensystem der Blöcke
 * @param {Object} rahmen  { links, oben, rechts, unten } im selben System
 */
export function ragtHinaus(block, rahmen) {
  if (!block || !rahmen || block.left == null || block.top == null) return false;
  return block.left < rahmen.links
      || block.top  < rahmen.oben
      || block.left + (block.width  ?? 0) > rahmen.rechts
      || block.top  + (block.height ?? 0) > rahmen.unten;
}

/**
 * Gleiche Abstände: Wo müsste der bewegte Block stehen, damit die Lücke zum Nachbarn
 * darüber genauso groß ist wie die zum Nachbarn darunter?
 *
 * Waagerecht gibt es das bewusst nicht — die Blöcke des Briefes stehen untereinander,
 * nebeneinander liegende Nachbarn kommen praktisch nicht vor.
 *
 * @param {Object} bewegt   { top, height } des gezogenen Blocks in Rohlage
 * @param {Array}  nachbarn [{ top, height }] der übrigen Blöcke
 * @returns {{ top:number, abstand:number, oben:{von:number,bis:number},
 *            unten:{von:number,bis:number} } | null} `null`, wenn oben oder unten
 *          kein Nachbar steht oder die Blöcke sich überlappen würden.
 */
export function gleicherAbstand(bewegt, nachbarn = []) {
  if (!bewegt || bewegt.top == null) return null;
  const unterkante = bewegt.top + (bewegt.height ?? 0);

  let oben = null, unten = null;
  for (const n of nachbarn) {
    if (!n || n.top == null) continue;
    const nUnten = n.top + (n.height ?? 0);
    if (nUnten <= bewegt.top && (oben == null || nUnten > oben)) oben = nUnten;
    if (n.top >= unterkante && (unten == null || n.top < unten)) unten = n.top;
  }
  if (oben == null || unten == null) return null;

  const top = Math.round((oben + unten - (bewegt.height ?? 0)) / 2);
  const abstand = top - oben;
  if (abstand < 0) return null;

  return {
    top,
    abstand,
    oben:  { von: oben, bis: top },
    unten: { von: top + (bewegt.height ?? 0), bis: unten },
  };
}

/**
 * Größenändern mit Einrasten: wie `resizeMitAnker`, nur dass die angefasste
 * senkrechte Kante an die nächstgelegene Hilfslinie springt.
 *
 * Senkrecht wird bewusst nicht eingerastet — bei Eckgriffen folgt die Höhe zwingend
 * der Breite, ein zweites Einrasten würde dem ersten widersprechen.
 *
 * @param {Array<number>} punkte   Linien, an denen eingerastet werden darf
 * @param {number} toleranz        Fangreichweite in Pixeln
 * @returns {{ ...resizeMitAnker, linie: number|null }} `linie` ist die getroffene
 *          Hilfslinie oder `null` — dann ist auch nichts anzuzeigen.
 */
export function resizeMitEinrasten({ griff, start, delta, punkte = [], toleranz = 12, minBreite = 40 }) {
  const roh = resizeMitAnker({ griff, start, delta, minBreite });
  // Waagerechte Griffe verändern die senkrechten Kanten überhaupt nicht — an
  // senkrechten Hilfslinien gibt es für sie also nichts einzurasten.
  if (griff[1] === 'm') return { ...roh, linie: null };
  const kante = (r) => (griff[1] === 'l' ? r.left : r.left + r.width);

  let linie = null, bester = toleranz;
  for (const pt of punkte) {
    const d = Math.abs(kante(roh) - pt);
    if (d < bester) { bester = d; linie = pt; }
  }
  if (linie == null) return { ...roh, linie: null };

  // Die Kante folgt der Zeigerbewegung eins zu eins, die Korrektur geht also direkt
  // in das Delta. Nur an der Mindestbreite greift sie nicht — dann wäre auch die
  // angezeigte Linie gelogen, und es bleibt beim rohen Ergebnis.
  const korrigiert = resizeMitAnker({
    griff, start, minBreite,
    delta: { x: delta.x + (linie - kante(roh)), y: delta.y },
  });
  return Math.abs(kante(korrigiert) - linie) < 0.5
    ? { ...korrigiert, linie }
    : { ...roh, linie: null };
}


/**
 * Rechteck um mehrere Blöcke — daran rastet ein gemeinsamer Zug ein.
 * Blöcke ohne Position werden übergangen; bleibt nichts übrig, kommt `null` zurück.
 *
 * @param {string[]} keys    die beteiligten Blöcke
 * @param {Object} offsets   gespeicherte Positionen aller Blöcke
 * @param {Object} hoehen    gemessene Höhen, je Block — die steht nicht in den Offsets
 */
export function gruppenHuelle(keys = [], offsets = {}, hoehen = {}) {
  let l = Infinity, t = Infinity, r = -Infinity, b = -Infinity;
  for (const k of keys) {
    const o = offsets[k];
    if (!o || o.left == null || o.top == null) continue;
    l = Math.min(l, o.left);
    t = Math.min(t, o.top);
    r = Math.max(r, o.left + (o.width ?? 0));
    b = Math.max(b, o.top + (hoehen[k] ?? 0));
  }
  return l === Infinity ? null : { left: l, top: t, width: r - l, height: b - t, keys };
}

/**
 * Welcher Versatz bei einem Tastendruck wirklich genommen wird.
 *
 * Eingerastet wird nur nach vorn, also in die Richtung, in die die Taste zeigt. Eine
 * Hilfslinie hinter dem Block würde ihn sonst festhalten: Er sitzt darauf, man drückt
 * einen Schritt weiter, und die Einrast-Rechnung zöge ihn sofort wieder zurück.
 *
 * @param {Object} roh          gewünschter Schritt, z. B. { x: 1, y: 0 }
 * @param {Object} eingerastet  Versatz, den die Einrast-Rechnung vorschlägt
 */
export function tastenVersatz(roh, eingerastet) {
  const proAchse = (a) =>
    roh[a] !== 0 && Math.sign(eingerastet[a]) === Math.sign(roh[a]) ? eingerastet[a] : roh[a];
  return { x: proAchse('x'), y: proAchse('y') };
}

export function seitenAnzahl(offsets = {}, hoehen = {}, pageH) {
  if (!pageH) return 1;
  return Math.max(1, Math.ceil(layoutHoehe(offsets, hoehen) / pageH));
}
